import { useState, useCallback } from 'react';
import { AxiosError } from 'axios';
import { ApiError } from '../types';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseApiReturn<T> extends UseApiState<T> {
  execute: (...args: any[]) => Promise<T | null>;
  setData: (data: T | null) => void;
  reset: () => void;
}

/**
 * Custom hook for making API calls with loading and error states
 *
 * @example
 * const { data, loading, error, execute } = useApi(doctorsService.getDoctors);
 *
 * useEffect(() => {
 *   execute();
 * }, [execute]);
 */
export function useApi<T>(
  apiFunction: (...args: any[]) => Promise<T>
): UseApiReturn<T> {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(
    async (...args: any[]): Promise<T | null> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const data = await apiFunction(...args);
        setState({ data, loading: false, error: null });
        return data;
      } catch (err) {
        const axiosError = err as AxiosError<ApiError>;
        const responseData = axiosError.response?.data;

        let errorMessage =
          responseData?.error ||
          responseData?.message ||
          responseData?.detail ||
          responseData?.non_field_errors?.[0];

        if (!errorMessage && responseData) {
          const firstFieldError = Object.values(responseData).find(
            (value) => Array.isArray(value) && value.length > 0
          );

          if (firstFieldError) {
            errorMessage = (firstFieldError as string[])[0];
          }
        }

        errorMessage =
          errorMessage ||
          axiosError.message ||
          'An error occurred';

        setState({ data: null, loading: false, error: errorMessage });
        return null;
      }
    },
    [apiFunction]
  );

  const setData = useCallback((data: T | null) => {
    setState((prev) => ({ ...prev, data }));
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, execute, setData, reset };
}

/**
 * Custom hook for making API calls that execute immediately
 *
 * @example
 * const { data, loading, error, refetch } = useApiCall(
 *   () => doctorsService.getDoctors(),
 *   []
 * );
 */
export function useApiCall<T>(
  apiFunction: () => Promise<T>,
  deps: React.DependencyList = []
): UseApiReturn<T> & { refetch: () => Promise<T | null> } {
  const { data, loading, error, execute, setData, reset } = useApi(apiFunction);

  // Auto-execute on mount and when deps change
  // Note: The consumer should call execute() in useEffect

  const refetch = useCallback(() => {
    return execute();
  }, [execute]);

  return { data, loading, error, execute, setData, reset, refetch };
}

/**
 * Custom hook for pagination
 */
export function usePaginatedApi<T>(
  apiFunction: (page: number, pageSize: number) => Promise<{ items: T[]; total_pages: number; total_items: number }>
) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const fetchPage = useCallback(
    async (pageNum: number, size: number = pageSize) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiFunction(pageNum, size);
        setItems(response.items);
        setTotalPages(response.total_pages);
        setTotalItems(response.total_items);
        setPage(pageNum);
        setPageSize(size);
      } catch (err) {
        const axiosError = err as AxiosError<ApiError>;
        const errorMessage =
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Failed to fetch data';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [apiFunction, pageSize]
  );

  const nextPage = useCallback(() => {
    if (page < totalPages) {
      fetchPage(page + 1);
    }
  }, [page, totalPages, fetchPage]);

  const prevPage = useCallback(() => {
    if (page > 1) {
      fetchPage(page - 1);
    }
  }, [page, fetchPage]);

  const goToPage = useCallback(
    (pageNum: number) => {
      if (pageNum >= 1 && pageNum <= totalPages) {
        fetchPage(pageNum);
      }
    },
    [totalPages, fetchPage]
  );

  return {
    items,
    loading,
    error,
    page,
    pageSize,
    totalPages,
    totalItems,
    fetchPage,
    nextPage,
    prevPage,
    goToPage,
    setPageSize: (size: number) => fetchPage(1, size),
    hasNextPage: page < totalPages,
    hasPrevPage: page > 1,
  };
}

export default useApi;
